import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { SettingCard, Field, SaveButton, useSave } from "./SettingsBits";

export function EmailCard({ email, hasPassword, onSaved }) {
  const [next, setNext] = useState("");
  const [password, setPassword] = useState("");
  const [busy, save] = useSave("put", "/account/email", (d) => { onSaved(d); setNext(""); setPassword(""); });
  return (
    <SettingCard title="Email address" body={`Signed in as ${email}.${hasPassword ? " Confirm with your password to change it." : " Set a password below before changing your email."}`} testId="account-email-card">
      <form className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end" onSubmit={(e) => { e.preventDefault(); save({ email: next, password }, "Email updated. We sent a notice to your old address."); }}>
        <Field label="New email"><Input type="email" value={next} onChange={(e) => setNext(e.target.value)} required disabled={!hasPassword} data-testid="account-email-input" /></Field>
        <Field label="Current password"><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required disabled={!hasPassword} autoComplete="current-password" data-testid="account-email-password" /></Field>
        <SaveButton busy={busy || !hasPassword} testId="account-email-save">Change email</SaveButton>
      </form>
    </SettingCard>
  );
}

export function PasswordCard({ hasPassword, onSaved }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [busy, save] = useSave("put", "/account/password", (d) => { onSaved(d); setCurrent(""); setNext(""); setAgain(""); });
  const submit = (e) => {
    e.preventDefault();
    if (next !== again) return toast.error("The new passwords don't match.");
    save({ current_password: hasPassword ? current : null, new_password: next }, hasPassword ? "Password changed. Other devices were signed out." : "Password set. You can now sign in with email too.");
  };
  return (
    <SettingCard title={hasPassword ? "Change password" : "Set a password"} body={hasPassword ? "At least 8 characters. Changing it signs you out everywhere else." : "You signed up with Google. Add a password to also sign in with email."} testId="account-password-card">
      <form className="grid gap-3 sm:grid-cols-3 sm:items-end" onSubmit={submit}>
        {hasPassword && <Field label="Current password"><Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} required autoComplete="current-password" data-testid="account-password-current" /></Field>}
        <Field label="New password"><Input type="password" value={next} onChange={(e) => setNext(e.target.value)} required minLength={8} autoComplete="new-password" data-testid="account-password-new" /></Field>
        <Field label="Repeat new password"><Input type="password" value={again} onChange={(e) => setAgain(e.target.value)} required minLength={8} autoComplete="new-password" data-testid="account-password-again" /></Field>
        <div className={hasPassword ? "sm:col-span-3" : ""}><SaveButton busy={busy} testId="account-password-save">{hasPassword ? "Change password" : "Set password"}</SaveButton></div>
      </form>
    </SettingCard>
  );
}

const PREFS = [
  { key: "notify_post_status", label: "Blog: when my post is approved or returned", group: "notifications" },
  { key: "notify_comments", label: "Blog: when someone comments on my post", group: "notifications" },
  { key: "newsletter_updates", label: "QuestionGod.org updates — new features, new question sets, community news", group: "newsletter" },
  { key: "newsletter_devotional", label: "Question-type devotional — a short reflection built around one type of question", group: "newsletter" },
];

export function PrefsCard({ prefs, onSaved }) {
  const [state, setState] = useState(prefs);
  const [busy, save] = useSave("put", "/account/preferences", onSaved);
  const rows = (group) => PREFS.filter((p) => p.group === group).map((p) => (
    <label key={p.key} className="flex items-center justify-between gap-4 py-2 text-sm text-foreground">
      <span>{p.label}</span>
      <Switch checked={!!state[p.key]} onCheckedChange={(v) => setState((s) => ({ ...s, [p.key]: v }))} data-testid={`pref-${p.key}`} />
    </label>
  ));
  return (
    <SettingCard title="Email preferences" body="Choose what lands in your inbox. You can change this any time." testId="account-prefs-card">
      <form onSubmit={(e) => { e.preventDefault(); save(state, "Preferences saved."); }}>
        <p className="font-mono text-[11px] uppercase tracking-wider text-gold">Notifications</p>
        {rows("notifications")}
        <p className="mt-4 font-mono text-[11px] uppercase tracking-wider text-gold">Newsletter</p>
        {rows("newsletter")}
        <div className="mt-4"><SaveButton busy={busy} testId="account-prefs-save">Save preferences</SaveButton></div>
      </form>
    </SettingCard>
  );
}
